// Working hours window for the per-task "Working hours only" flag.
// Centralized so the time picker, calendar enforcement, filter, and reschedule
// cron all read the same definition. The reschedule edge function keeps its
// own copy of the helpers in Deno but mirrors the same constants.
export const WORK_START_HOUR = 9;
export const WORK_END_HOUR = 17;
export const WORK_DAY_INDICES: readonly number[] = [1, 2, 3, 4, 5]; // Sun=0..Sat=6
export const WORK_SLOT_MINUTES = 15;

// Parse a YYYY-MM-DD string as a *local* Date at midnight. Avoids the
// `new Date("2026-05-25")` trap (which parses as UTC and can report the
// wrong local weekday near midnight).
export function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// A calendar event's `start` is commonly built as a naive local timestamp
// (`${dateStr}T${timeStr}`, no timezone suffix — FullCalendar parses that as
// local time). Pairing it with a `.toISOString()` end (UTC-suffixed) mixes
// two different zone interpretations for the same event and throws off
// FullCalendar's own duration/position math by the local UTC offset. This
// formats a Date the same naive-local way, so `end` matches `start`.
export function formatLocalDateTime(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${formatLocalDate(d)}T${h}:${min}:${s}`;
}

export function isWorkingDay(dateStr: string): boolean {
  const day = parseLocalDate(dateStr).getDay();
  return WORK_DAY_INDICES.includes(day);
}

// Return `dateStr` itself if it's already a working day, otherwise advance
// to the next Mon–Fri.
export function nextWorkingDay(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  while (!WORK_DAY_INDICES.includes(d.getDay())) {
    d.setDate(d.getDate() + 1);
  }
  return formatLocalDate(d);
}

// A null time means "all-day", which is valid on a working day.
export function isStartTimeInWorkingHours(time: string | null): boolean {
  if (!time) return true;
  const [hh, mm] = time.split(":").map(Number);
  const minutes = hh * 60 + mm;
  return minutes >= WORK_START_HOUR * 60 && minutes < WORK_END_HOUR * 60;
}

// Composite check used by the "Working hours only" toggle: a task may turn
// the flag on if it has no date yet, or its current date/time falls within
// the window. A null date is fine — future placement is constrained by the
// pickers, so we don't need a date to exist to enable the flag.
export function isValidWorkHoursSlot(
  date: string | null,
  time: string | null,
): boolean {
  if (date === null) return true;
  if (!isWorkingDay(date)) return false;
  return isStartTimeInWorkingHours(time);
}

// Discrete 15-min start slots inside the window. Last slot is 16:45.
export function getWorkHoursTimeSlots(
  slotMinutes: number = WORK_SLOT_MINUTES,
): string[] {
  const slots: string[] = [];
  const start = WORK_START_HOUR * 60;
  const end = WORK_END_HOUR * 60;
  for (let t = start; t < end; t += slotMinutes) {
    const hh = String(Math.floor(t / 60)).padStart(2, "0");
    const mm = String(t % 60).padStart(2, "0");
    slots.push(`${hh}:${mm}`);
  }
  return slots;
}
